import { Component, OnInit, ViewEncapsulation, Input, Output, EventEmitter, OnDestroy } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, Validators, UntypedFormGroup } from '@angular/forms';
import { ReducerUtilityService } from 'src/app/shared/utility-services/reducer-utility.service';
import { County } from 'src/app/angular-elements/shared/interfaces/zip-county-response.interface';
import { NgRedux, select } from '@ptl/angular-redux-store';
import { StateStructure } from '@ptl/state';
import { ProducerService } from '../producer.service';
import {
  LOGIN_INFO_REDUCER_NAME,
  UserInfoStateStructure,
  SeniorAgency,
  LoggedInUserInformationService,
  EntitlementsHelperService,
  LINE_OF_BUSINESS_ENUM,
  IndividualMarketAgency,
  BaseAgency,
  setSelectedRegionOrState,
  setAgencyPreferences,
  setLobAgencyPreferences,
} from '@ptl/account-management';
import { CommonUtilityService } from 'src/app/shared/utility-services/common-utility.service';
import { BooleanInput, GlobalShadowStyleDirective, PtlWindowLocationService } from '@ptl/global-components';
import { ZipCountyStateStructure } from 'src/app/angular-elements/shared/interfaces/zipCountyStateStructure.interface';
import { PrePopulatedDataStateStructure } from '../../shared/components/configs/interfaces/prePopulatedDataStateStructure.interface';
import { AuthoringModeService } from 'src/app/shared/services/authoring-mode.service';
import { OtherUserInfo } from '../configs/interfaces/otherUserInfo.interface';
import { saveAgency, saveOtherUserInfo } from '../configs/reducers/producer.reducer';
import { Observable, Subscription } from 'rxjs';
import { BrandingConfigurationService } from '@ptl/brand';
import { ZIP_COUNTY_STATE_NAME } from '../../demographics/zip-county-modal/reducers/zipCounty.reducer';
import { PRE_POPULATED_DATA_STATE_NAME } from '../../shared/components/configs/reducers/prePopulatedData.reducer';

export interface AgencyFormControls {
  agency: AbstractControl;
  region: AbstractControl;
  npnNumber: AbstractControl;
  rememberSelection: AbstractControl;
  isOnCallWithFieldAgent: AbstractControl;
}

export interface AgencyFormAsJson {
  agency: SeniorAgency | IndividualMarketAgency;
  region: string;
  npnNumber: string;
  rememberSelection: boolean;
  isOnCallWithFieldAgent: boolean;
}

@Component({
    selector: 'app-producer-change-agency',
    templateUrl: './producer-change-agency.component.html',
    styleUrls: ['./producer-change-agency.component.scss'],
    encapsulation: ViewEncapsulation.ShadowDom,
    standalone: false
})
export class ProducerChangeAgencyComponent extends GlobalShadowStyleDirective implements OnInit, OnDestroy {
  // These inputs will only come from AEM
  @Input() @BooleanInput() hideZipcode: boolean;
  @Input() @BooleanInput() enableSecondButton: boolean;
  @Input() buttonOneLabel: string;
  @Input() buttonTwoLabel: string;
  @Input() viewPlanLink: string;
  @Input() buttonTwoLink: string;
  @Input() @BooleanInput() noBrandPageRefresh: boolean; // will map into "ignoreCookieUpdates" input below

  // These inputs are only used through local components so booleans are allowed here
  @Input() enrollmentSubmitMode: boolean = false;
  @Input() isAgencyOnlyMode: boolean = false;
  @Input() showRegionFilter: boolean = false;
  @Input() ignoreCookieUpdates: boolean = false;

  // This input is used by both AEM and local components (AEM must ensure the input string matches a LINE_OF_BUSINESS_ENUM)
  @Input() selectedMarket: LINE_OF_BUSINESS_ENUM;

  // This output can only be used by local components
  @Output() agencyChangeEvent: EventEmitter<BaseAgency> = new EventEmitter<BaseAgency>();

  @select(['appStates', 'LoginInfo'])
  loginInfo$: Observable<UserInfoStateStructure>;
  loginInfoSubscription: Subscription;

  agencyForm: UntypedFormGroup;
  loginInfo: UserInfoStateStructure;

  availableRegions: string[];
  availableAgencies: BaseAgency[];
  agenciesForDisplay: BaseAgency[];
  npnPlaceholderText: string;

  /** Stores the selected county */
  saveableCounty: County;
  saveableZipCode: string;
  zipCodeRegion: string;
  currentBrandCookie: string;
  stateValues = {
    zipCode: null,
    county: null
  };

  hideZipcodeBool: boolean = false;
  regionMismatch: boolean = false;
  isFieldAgent: boolean = false;
  isAuthoring: boolean = false;
  isInternal: boolean = false;
  showSecondButton: boolean = false;
  hasPageLoadedOnce: boolean = false;

  constructor(
    private fb: UntypedFormBuilder,
    private reducerUtilityService: ReducerUtilityService,
    private ngRedux: NgRedux<StateStructure>,
    private producerService: ProducerService,
    private commonUtilityService: CommonUtilityService,
    private loggedInUserService: LoggedInUserInformationService,
    private windowLocationService: PtlWindowLocationService,
    private entitlementsHelperService: EntitlementsHelperService,
    private authoringMode: AuthoringModeService,
    private brandService: BrandingConfigurationService
  ) {
    super();
    this.regionMismatch = false;
    this.agencyForm = this.fb.group({
      region: this.fb.control(''),
      agency: this.fb.control('', [Validators.required]),
      npnNumber: this.fb.control('', Validators.required),
      rememberSelection: this.fb.control(false),
      isOnCallWithFieldAgent: this.fb.control(false)
    });
  }

  get formControls(): AgencyFormControls {
    return this.agencyForm.controls as unknown as AgencyFormControls;
  }

  get formValuesAsJson(): AgencyFormAsJson {
    return this.agencyForm.value as unknown as AgencyFormAsJson;
  }

  async ngOnInit() {
    if (this.authoringMode.getAuthoringMode()) {
      this.isAuthoring = true;
      return;
    }

    // TODO - this may be deprecated since we now have a designated page for loading Agent Cubed incoming requests
    //
    // We need a subscription here because we can land on this page directly from an outside source like Agent Cubed
    // and the login services may need to run before this is available
    this.loginInfoSubscription = this.loginInfo$.subscribe((loginInfo: UserInfoStateStructure) => {
      if (!this.hasPageLoadedOnce && loginInfo?.userInfo?.agencyInfo) {
        // Configure this components basic values and boolean flags
        this.initializeSettings();

        // Populate initial values based on saved data
        this.initializeValues();

        this.hasPageLoadedOnce = true;
      }
    });
  }

  ngOnDestroy() {
    if (this.loginInfoSubscription) {
      this.loginInfoSubscription.unsubscribe();
    }
  }

  initializeSettings() {
    this.loginInfo = this.reducerUtilityService.getReducerFromApplicationState(LOGIN_INFO_REDUCER_NAME) as UserInfoStateStructure;
    this.showSecondButton = this.enableSecondButton;
    this.npnPlaceholderText = this.selectedMarket === LINE_OF_BUSINESS_ENUM.SENIOR ? 'ABC5555555' : 'Agency Id';
    this.ignoreCookieUpdates = this.ignoreCookieUpdates || this.noBrandPageRefresh;

    // TODO: this needs to be smart for b360 or not by region.
    this.availableAgencies = this.entitlementsHelperService.getAgenciesByLineOfBusiness(this.selectedMarket, this.showRegionFilter);
    this.isFieldAgent = this.loggedInUserService.isMedicareFieldAgent();

    // Hide everything else if we're in the bare agency change mode or if it was configured through AEM
    if (this.isAgencyOnlyMode || this.hideZipcode) {
      this.hideZipcodeBool = true;
    }

    // Set the internal flag
    if (this.loginInfo.userInfo.role === "INTERNAL") {
      this.isInternal = true;
    }
  }

  initializeValues() {
    // Initialize zipcode and county based off of the state and/or prepop data
    this.initializeZipCountyValues();

    // default will show all agencies
    this.agenciesForDisplay = this.availableAgencies;

    // Initialize region dropdown values
    this.availableRegions = [];
    for (let i = 0; i < this.availableAgencies.length; i++) {
      if (this.availableRegions.indexOf(this.availableAgencies[i].region) === -1) {
        this.availableRegions.push(this.availableAgencies[i].region);
      }
    }

    // Preload values based on currently selected agency if it exists
    let selectedAgency = this.entitlementsHelperService.getSelectedAgency(this.selectedMarket);
    if (selectedAgency) {
      this.formControls.region.setValue(selectedAgency.region);
      this.formControls.agency.setValue(selectedAgency);
      this.setNpnNumber(selectedAgency);
    } else {
      if (this.availableRegions.length === 1) {
        this.formControls.region.setValue(this.availableRegions[0]);
      }
    }

    this.updateAgencyList();
  }

  initializeZipCountyValues() {
    const prePopulateData: PrePopulatedDataStateStructure = this.reducerUtilityService.getState(PRE_POPULATED_DATA_STATE_NAME);
    const zipcountyState: ZipCountyStateStructure = this.reducerUtilityService.getState(ZIP_COUNTY_STATE_NAME);
    this.currentBrandCookie = this.brandService.getCurrentBrand().cookieBrandCode;

    if (zipcountyState.county) {
      this.stateValues.zipCode = zipcountyState.zipCode;
      this.stateValues.county = zipcountyState.county.county;
    }
    // TODO - This may be deprecated since we no longer come to this page immediately from Agent Cubed
    // there is an interim page that loads all the data and redirects the user
    /**
     * Update 2024-11-12, Devon: this flow was being entered, but then the values were being pulled from "zipcountyState", 
     * even though it seems like this is intended to be a prePopulateData field
     * This change just adds a check on zipcountyState [ so as to maintain existing behavior, regardless of consequence ]
     * and uses the prePopulateData info
     */
    else if (prePopulateData?.prepopulatedFields?.zipCode) {
      this.stateValues.zipCode = zipcountyState?.zipCode || prePopulateData.prepopulatedFields.zipCode;
      this.stateValues.county = zipcountyState?.county?.county || prePopulateData.prepopulatedFields.county;

      // If we don't have state values we need to make sure the state and brand are populated using this method
      this.commonUtilityService.setBrandByZipCode(this.stateValues.zipCode, this.stateValues.county);
    }
  }

  // Agency Name Formatter, it also formats abbreviations to uppercase without affecting pascal cases agency names.
  formatAgencyName(name: string) {
    if(!name) {
      return 'INTERNAL ROLE Agency'
    }
    const abbreviations = ["LLC", "INC", "CO", "CORP", "LTD"];

    return name.split(" ").map((word) => {
      if (abbreviations.includes(word.toUpperCase())) {
        return word.toUpperCase();
      } else {
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      }
    }).join(" ");
  }

  // Filter the agency list by region if it is applied
  updateAgencyList() {
    if (!this.formValuesAsJson.region) {
      return;
    }
    this.agenciesForDisplay = this.availableAgencies.filter((agency) => !this.showRegionFilter || agency.region === this.formValuesAsJson.region);


    this.agenciesForDisplay = this.agenciesForDisplay.map((agency) => {
      const agencyName = agency.agencyName || '';
      return {
        ...agency, // spread existing properties
        sortableName: agencyName.toLowerCase(), // add sortableName
      }
    })

    this.agenciesForDisplay.sort((a, b) => {
      return a.sortableName.localeCompare(b.sortableName);
    })

    if (this.agenciesForDisplay.length === 1) {
      this.formControls.agency.setValue(this.agenciesForDisplay[0]);
      this.setNpnNumber(this.agenciesForDisplay[0]);
    }
  }

  agencyChange(selectedAgency: SeniorAgency | IndividualMarketAgency) {
    this.setNpnNumber(selectedAgency);
    this.formControls.npnNumber.markAsTouched();

    this.checkRegionMismatch();

    if (!this.formValuesAsJson.region) {
      this.formControls.region.setValue(selectedAgency.region);
    }

    this.refreshPageOnBrandChange();
  }

  setNpnNumber(agency: BaseAgency) {
    if (LINE_OF_BUSINESS_ENUM.SENIOR === this.selectedMarket) {
      // NPN is pulled from the user for all Senior agencies~
      this.formControls.npnNumber.setValue(this.loginInfo.userInfo.npnNo || agency.npnNo);
    } else {
      // defaults to user's npn or the agency ID if not present
      this.formControls.npnNumber.setValue(this.loginInfo.userInfo?.npnNo || agency.agencyId);
    }
  }

  /** Updates state and service data */
  async updateData() {
    // We are defining this page as the beginning of the application flow, so we are going to clear out all
    // application data at this point to start out completely fresh
    this.commonUtilityService.resetStateForNewApplicationFlow();

    const selectedAgency: BaseAgency = this.formValuesAsJson.agency;
    const selectedAgencyForOldState: BaseAgency = this.formValuesAsJson.agency;
    const otherUserInformation: OtherUserInfo = {
      isOnCallWithFieldAgent: this.formValuesAsJson.isOnCallWithFieldAgent
    };
    selectedAgency.npnNo = this.formValuesAsJson.npnNumber;
    this.ngRedux.dispatch(saveAgency(selectedAgencyForOldState));
    this.ngRedux.dispatch(saveOtherUserInfo(otherUserInformation));

    // save the selected agency
    if (this.formValuesAsJson.rememberSelection) {
      // TODO - this will change when the backend can support agency preferences by LOB
      this.ngRedux.dispatch(setAgencyPreferences(this.formValuesAsJson.agency.agencyId, this.formValuesAsJson.agency.region))

      await this.producerService.updateAgencyPref(
        this.loginInfo.userId,
        selectedAgency.agencyId,
        selectedAgency.region
      );

      this.loginInfo.userInfo.agencyInfo.preferredAgencies[this.selectedMarket] = selectedAgency;
    }

    // Select this agency and emit that value
    this.loginInfo.userInfo.agencyInfo.selectedAgencies[this.selectedMarket] = selectedAgency;
    this.ngRedux.dispatch(setLobAgencyPreferences(this.loginInfo.userInfo.agencyInfo));

    // We should not use this reducer anymore, but leaving this update in for potential older uses
    this.ngRedux.dispatch(setSelectedRegionOrState(selectedAgency.region));

    if (!this.hideZipcodeBool && !this.isAgencyOnlyMode) {
      // Only update brand here after submitting the form
      this.brandService.setBrandCookie(this.saveableCounty.brand);
      this.brandService.setDdTokenCookie(this.saveableCounty.brand);

      this.commonUtilityService.saveCountyZipData(
        this.saveableCounty,
        this.saveableZipCode
      );
    }
  }

  async submit(buttonLabel: string) {
    if (!this.enrollmentSubmitMode) {
      await this.updateData();
    }

    if (this.isAgencyOnlyMode) {
      const selectedAgency: BaseAgency = this.formValuesAsJson.agency;
      this.agencyChangeEvent.emit(selectedAgency);
    } else {
      if (buttonLabel === this.buttonOneLabel) {
        this.windowLocationService.goToUrl(this.viewPlanLink);
      } else if (buttonLabel === this.buttonTwoLabel) {
        this.windowLocationService.goToUrl(this.buttonTwoLink);
      }
    }
  }

  cancel() {
    this.agencyChangeEvent.emit(null);
  }

  async zipCountyChanged(value: { county: County; zipCode: string }) {
    if (value?.county) {
      this.saveableCounty = value.county;
      this.saveableZipCode = value.zipCode;
      this.zipCodeRegion = value.county.region;
      this.checkRegionMismatch();
      this.refreshPageOnBrandChange();
    }
    else {
      this.saveableCounty = null;
      this.saveableZipCode = null;
      this.zipCodeRegion = null;
    }
  }

  async refreshPageOnBrandChange() {
    // Conditions that will ignore the refresh command
    if (!this.saveableCounty || this.regionMismatch || this.ignoreCookieUpdates || this.isAgencyOnlyMode) {
      return;
    }

    //Only refresh when the brand has changed
    let newBrandCookie = this.brandService.getCurrentBrand().cookieBrandCode;
    if (this.currentBrandCookie !== newBrandCookie) {
      this.agencyForm.disable();
      await this.updateData();
      setTimeout(() => {
        window.location.reload();
      }, 100);
    }
  }

  checkRegionMismatch() {
    const selectedAgency: SeniorAgency | IndividualMarketAgency = this.formValuesAsJson.agency;
    const isTelesalesFlow = this.loggedInUserService.isMedicareTelesales();

    if (this.saveableCounty && !isTelesalesFlow && this.commonUtilityService.isAgent()) {
      if (this.selectedMarket === LINE_OF_BUSINESS_ENUM.SENIOR) {
        this.regionMismatch = this.saveableCounty.state !== selectedAgency.region;
      }
      else {
        // Zip-County returns sub regions like "WPA"
        // need state fallback because some agencies still use the high level "PA" region.
        this.regionMismatch = !(this.saveableCounty.region === selectedAgency.region || this.saveableCounty.state === selectedAgency.region);
      }
    }
  }

  getPreferredAgencyHint() {
    let prefAgency = this.entitlementsHelperService.getPreferredAgency(this.selectedMarket);

    if (!prefAgency) {
      return 'You do not have a Preferred Agency selected.';
    } else if (this.isAgencySelected(prefAgency)) {
      return 'Your Preferred Agency is selected.';
    } else {
      return `Your Preferred Agency is ${prefAgency.agencyName} - ${prefAgency.region}`
    }
  }

  isAgencySelected(agency: BaseAgency): boolean {
    return !!agency &&
      agency.agencyId === this.formValuesAsJson.agency.agencyId &&
      agency.region === this.formValuesAsJson.agency.region;
  }

  updateRememberMeCheckboxVisibility(): boolean {
    let prefAgency = this.entitlementsHelperService.getPreferredAgency(this.selectedMarket);

    const shouldShowRememberMeCheckbox = this.formValuesAsJson.agency &&
      (!prefAgency ||
        (prefAgency.agencyId !== this.formValuesAsJson.agency.agencyId ||
          prefAgency.region !== this.formValuesAsJson.agency.region));

    if (!shouldShowRememberMeCheckbox) {
      this.formControls.rememberSelection.setValue(false);
    }

    return shouldShowRememberMeCheckbox && !this.enrollmentSubmitMode;
  }

  trackAgencyComparison(option: BaseAgency, value: BaseAgency) {
    return option.agencyId === value.agencyId && option.region === value.region;
  }
}
